import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  fetchInventoryFilters,
  type InventoryFilterOption,
} from "@/data/carsData";
import {
  Menu,
  X,
  ChevronDown,
  Home,
  Car,
  DollarSign,
  // Gift,
  // Wrench,
  Users,
  Phone,
  MapPin,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type NavDropdownItem = { label: string; href: string };
type NavItem = {
  name: string;
  icon: LucideIcon;
  href: string;
  dropdown?: NavDropdownItem[];
};

const BASE_NAV_ITEMS: NavItem[] = [
  { name: "Home", icon: Home, href: "#home" },
  {
    name: "Inventory",
    icon: Car,
    href: "#inventory",
    dropdown: [
      { label: "View All Inventory", href: "/car-inventory" },
      { label: "View All Sedans", href: "__SEDAN__" },
      { label: "View All SUVs", href: "__SUV__" },
      { label: "View $25 K and Under", href: "/car-inventory?priceMax=25000" },
      { label: "View $30 K and Under", href: "/car-inventory?priceMax=30000" },
      { label: "View $40 K and Under", href: "/car-inventory?priceMax=40000" },
      { label: "Quick Credit Approval", href: "#financing" },
    ],
  },
  {
    name: "Financing",
    icon: DollarSign,
    href: "#financing",
    dropdown: [
      { label: "Quick Credit Approval", href: "#financing" },
      { label: "Our Finance Center", href: "/finance" },
    ],
  },
  {
    name: "About Us",
    icon: Users,
    href: "#about",
    dropdown: [
      { label: "Directions", href: "/direction" },
      { label: "Contact Us", href: "/contact" },
      { label: "About Us", href: "/about" },
    ],
  },
];

// converts "#inventory" to "/#inventory" so it works from ANY page
const toRoute = (hashHref: string) => {
  if (!hashHref.startsWith("#")) return hashHref;
  return hashHref === "#home" ? "/" : `/${hashHref}`;
};

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  const [isScrolled, setIsScrolled] = useState<boolean>(!isHome);
  const showTopBar = true;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(
    null,
  );

  const [bodyStyleOptions, setBodyStyleOptions] = useState<
    InventoryFilterOption[]
  >([]);

  useEffect(() => {
    let isMounted = true;

    const loadBodyStyles = async () => {
      try {
        const filters = await fetchInventoryFilters();
        if (!isMounted) return;
        setBodyStyleOptions(filters.bodyStyles);
      } catch {
        if (!isMounted) return;
        setBodyStyleOptions([]);
      }
    };

    loadBodyStyles();

    return () => {
      isMounted = false;
    };
  }, []);

  const getBodyStyleHref = (label: string) => {
    const matched = bodyStyleOptions.find(
      (option) =>
        option.label.trim().toLowerCase() === label.trim().toLowerCase(),
    );

    return matched
      ? `/car-inventory?bodyStyle=${encodeURIComponent(matched.value)}`
      : "/car-inventory";
  };

  const navItems = useMemo<NavItem[]>(() => {
    return BASE_NAV_ITEMS.map((item) => {
      if (item.name !== "Inventory" || !item.dropdown) return item;

      return {
        ...item,
        dropdown: item.dropdown.map((dropdownItem) => {
          if (dropdownItem.href === "__SEDAN__") {
            return {
              ...dropdownItem,
              href: getBodyStyleHref("Sedan"),
            };
          }

          if (dropdownItem.href === "__SUV__") {
            return {
              ...dropdownItem,
              href: getBodyStyleHref("SUV"),
            };
          }

          return dropdownItem;
        }),
      };
    });
  }, [bodyStyleOptions]);

  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setOpenDropdown(null);
    setOpenMobileDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = navRef.current;
      if (!el) return;
      const target = e.target;
      if (target instanceof Node && !el.contains(target)) setOpenDropdown(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!isHome) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isHome]);

  return (
    <motion.nav
      ref={navRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome && !isScrolled
          ? "bg-transparent shadow-none"
          : "bg-white shadow-lg"
      }`}
    >
      <div
        className={`overflow-hidden transition-all duration-300 ${
          showTopBar
            ? "max-h-10 opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`text-white ${
            isHome && !isScrolled
              ? "bg-gray-900/95 backdrop-blur-sm"
              : "bg-gray-900"
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-10 flex items-center justify-center md:justify-end gap-3 text-[11px] sm:text-xs font-medium">
              <a
                href="tel:+16043588081"
                className="hover:text-white/85 transition-colors whitespace-nowrap inline-flex items-center gap-1"
              >
                <Phone className="w-3.5 h-3.5" />
                (604) 358-8081
              </a>
              <span className="opacity-70">|</span>
              <a
                href="https://www.google.com/maps/search/?api=1&query=20308+Logan+Ave,+Langley,+BC+V3A+4L7"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-1 hover:text-white/85 transition-colors"
                aria-label="Open dealership location in Google Maps"
                title="Open location in Google Maps"
              >
                <MapPin className="w-3.5 h-3.5" />
                20308 Logan Ave Langley, BC V3A 4L7
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300">
        <div className="h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 pl-2 lg:pl-3">
            <div
              className={`font-serif text-2xl font-bold tracking-tight ${
                isScrolled ? "text-brand-red" : "text-white"
              }`}
            >
              <span className="text-brand-red">BIG BOYS</span>
              <span className={isScrolled ? "text-brand-dark" : "text-white"}>
                {" "}
                MOTOR
              </span>
              <span className="text-brand-gold"> CLUB</span>
            </div>
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item, index) => {
              const hasDropdown =
                Array.isArray(item.dropdown) && item.dropdown.length > 0;
              const isOpen = openDropdown === item.name;

              const basePill =
                "px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1 transition-all duration-300";
              const pillStyle = isScrolled
                ? `text-gray-700 hover:text-brand-red hover:bg-red-50 ${isOpen ? "bg-red-50 text-brand-red" : ""}`
                : `text-white/90 hover:text-white hover:bg-white/10 ${isOpen ? "bg-white/20 text-white" : ""}`;

              const homeActive =
                item.name === "Home"
                  ? isScrolled
                    ? "bg-red-50 text-brand-red"
                    : "bg-white/20 text-white"
                  : "";

              if (!hasDropdown) {
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <Link
                      to={toRoute(item.href)}
                      onClick={() => setOpenDropdown(null)}
                      className={`${basePill} ${pillStyle} ${homeActive}`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                );
              }

              return (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    onClick={() => setOpenDropdown(isOpen ? null : item.name)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    className={`${basePill} ${pillStyle}`}
                  >
                    {item.name}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </motion.button>

                  {isOpen && (
                    <div
                      aria-hidden="true"
                      className="absolute left-0 top-full h-10 w-[280px] bg-transparent"
                    />
                  )}

                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 14, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      className="absolute left-0 mt-3 w-[280px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="py-2">
                        {(item.dropdown ?? []).map((d) => (
                          <Link
                            key={d.label}
                            to={toRoute(d.href)}
                            onClick={() => setOpenDropdown(null)}
                            className="block px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-red-50 hover:text-brand-red transition-colors"
                          >
                            {d.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop Contact */}
          <a
            href="tel:+16043588081"
            className={`hidden lg:flex items-center gap-2 px-6 py-2.5 rounded-full font-medium transition-all duration-300 ${
              isScrolled
                ? "bg-brand-red text-white hover:bg-red-700 shadow-glow-red"
                : "bg-white/10 text-white border border-white/30 hover:bg-white/20"
            }`}
          >
            <Phone className="w-4 h-4" />
            Contact Us
          </a>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${isScrolled ? "text-gray-700" : "text-white"}`}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white border-t"
        >
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => {
              const hasDropdown =
                Array.isArray(item.dropdown) && item.dropdown.length > 0;
              const isOpen = openMobileDropdown === item.name;

              if (!hasDropdown) {
                return (
                  <Link
                    key={item.name}
                    to={toRoute(item.href)}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setOpenMobileDropdown(null);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-red-50 hover:text-brand-red transition-colors"
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              }

              return (
                <div
                  key={item.name}
                  className="rounded-lg border border-gray-100 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenMobileDropdown(isOpen ? null : item.name)
                    }
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-brand-red transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="bg-white">
                      {(item.dropdown ?? []).map((d) => (
                        <Link
                          key={d.label}
                          to={toRoute(d.href)}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setOpenMobileDropdown(null);
                          }}
                          className="block pl-12 pr-4 py-3 text-sm font-semibold text-gray-700 hover:bg-red-50 hover:text-brand-red transition-colors"
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <a
              href="tel:+16043588081"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 mt-4 bg-brand-red text-white rounded-lg font-medium"
            >
              <Phone className="w-5 h-5" />
              Contact Us
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
